import axios from "axios";
import Cookies from 'js-cookie';
import { showError, showSuccess } from '@/utils/toast';

export const HttpHelper = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
  timeout: 30000, // 30 seconds timeout
});

// Add a request interceptor to include the token in all requests
HttpHelper.interceptors.request.use(
    (config) => {
        const token = Cookies.get('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle common errors
HttpHelper.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Handle different types of errors
        const { response, request, message } = error;

        // Handle different HTTP status codes
        if (response) {
            // Server responded with error status
            const { status, data } = response;
            
            switch (status) {
                case 400:
                    // Bad Request
                    showError(data?.message || 'Invalid request. Please check your input.');
                    break;
                    
                case 401:
                    // Unauthorized - Token expired or invalid
                    Cookies.remove('token');
                    localStorage.removeItem('sessiondata');
                    showError('Session expired. Please login again.');
                    
                    // Only redirect if we're not already on the login page
                    if (typeof window !== 'undefined' && !window.location.pathname.includes('/')) {
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 2000);
                    }
                    break;
                    
                case 403:
                    // Forbidden
                    showError(data?.message || 'Access denied. You do not have permission to perform this action.');
                    break;
                    
                case 404:
                    // Not Found
                    showError(data?.message || 'Resource not found.');
                    break;
                    
                case 422:
                    // Validation Error
                    if (data?.errors) {
                        // Handle validation errors
                        const errorMessages = Object.values(data.errors).flat();
                        showError(errorMessages.join(', '));
                    } else {
                        showError(data?.message || 'Validation failed. Please check your input.');
                    }
                    break;
                    
                case 429:
                    // Too Many Requests
                    showError('Too many requests. Please try again later.');
                    break;
                    
                case 500:
                    // Internal Server Error
                    showError('Server error. Please try again later.');
                    break;
                    
                case 502:
                case 503:
                case 504:
                    // Server Unavailable
                    showError('Service temporarily unavailable. Please try again later.');
                    break;
                    
                default:
                    // Other server errors
                    showError(data?.message || `Server error (${status}). Please try again.`);
            }
        } else if (request) {
            // Request was made but no response received
            if (error.code === 'ECONNABORTED') {
                showError('Request timeout. Please check your connection and try again.');
            } else {
                showError('Network error. Please check your internet connection.');
            }
        } else {
            // Something else happened
            showError('An unexpected error occurred. Please try again.');
        }
        
        return Promise.reject(error);
    }
);

